import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import SvgIcon from "../../../../parts/SvgIcon";
import {
  calculateMacros,
  formatCustomUnitMeasurementEquivalenceSuffix,
  parseUnitToShow,
  transformMealFoods,
} from "../../../../utils/nutrition-util";

const MasterMealsLibraryTable = ({ data }) => {
  const { t } = useTranslation("coaching-portal");
  const ns = "masterFoodLibraryScreen.table";
  const [expandedRows, setExpandedRows] = useState([]);
  const itemDetails = {
    itemProteins: undefined,
    itemCarbs: undefined,
    itemFats: undefined,
    itemCalories: undefined,
    itemFibers: undefined,
    foodKey: "foodDetails",
  };
  const parsedMeals = useMemo(() => {
    return data.map((item) => {
      const { foods = [] } = item;
      const parsedFoods = transformMealFoods(foods) || [];
      const macros = calculateMacros({
        isMeal: true,
        isRecipe: false,
        foods,
        itemDetails,
      });
      return {
        ...item,
        parsedFoods,
        macros,
      };
    });
  }, [data]);

  /**
   * Function to close and open the dropdwon
   * @param {*} index
   */
  const toggleRow = (index) => {
    setExpandedRows((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  /**
   * Open edit modal
   */
  const handleOpenModal = (_id) => {
    const body = { "modal[type]": "build-meal", mealId: _id };

    submit(`/actions/refresh/?`, {
      method: "GET",
      body,
    });
  };

  /**
   * Delete meal
   * @param {*} e
   * @param {*} _id
   */
  const deleteMeal = (e, _id) => {
    e.preventDefault();

    submit(`/actions/meal-planner/delete-master-food-library/${_id}/meal`, {
      method: "GET",
    });
  };

  return (
    <section className="meal-table" data-scrollable="true" role="table-wrapper">
      <table>
        <thead>
          <tr className="meals">
            <th className="big identifier">{t(`${ns}.foodName`)}</th>
            <th className="medium">{t(`${ns}.brandName`)}</th>
            <th className="medium">{t(`${ns}.quantity`)}</th>
            <th className="small">{t(`${ns}.proteins`)}</th>
            <th className="small">{t(`${ns}.carbs`)}</th>
            <th className="small">{t(`${ns}.fats`)}</th>
            <th className="small">{t(`${ns}.fibers`)}</th>
            <th className="medium">{t(`${ns}.kCal`)}</th>
            <th className="medium">{t(`${ns}.actions`)}</th>
          </tr>
        </thead>
        <tbody>
          {parsedMeals.map((item, index) => {
            const { name = "", parsedFoods = [], _id, macros } = item;
            const { proteins, carbs, fats, fibers, calories } = macros;
            return (
              <React.Fragment key={index}>
                <tr onClick={() => toggleRow(index)} className="expandable-row">
                  <td colSpan="10">
                    <div className="row-main">
                      <div className="row-summary">
                        <div className="identifier">
                          <SvgIcon
                            src={`/img/svg/angle-${
                              expandedRows.includes(index) ? "up" : "down"
                            }.svg`}
                            alt={t(`${ns}.openAlt`)}
                          />
                          <span>{name}</span>
                        </div>
                        <span></span>
                        <span>{t(`${ns}.oneServing`)}</span>
                        <span>{proteins}</span>
                        <span>{carbs}</span>
                        <span>{fats}</span>
                        <span>{fibers}</span>
                        <span>{calories}</span>
                        <div className="actions">
                          <button
                            data-button-type="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenModal(_id);
                            }}
                          >
                            <span>{t(`${ns}.edit`)}</span>
                          </button>
                          <button
                            data-button-type="secondary"
                            data-icon-only
                            onClick={(e) => deleteMeal(e, _id)}
                          >
                            <img
                              src="/img/new-icons/red-trash.svg"
                              alt={t(`${ns}.deleteAlt`)}
                            />
                          </button>
                        </div>
                      </div>

                      {expandedRows.includes(index) && (
                        <div className="row-details">
                          {parsedFoods.map(
                            (
                              {
                                brand = "",
                                name: foodName = "",
                                calories: foodCalories = 0,
                                carbs: foodCarbs = 0,
                                fats: foodFats = 0,
                                proteins: foodProteins = 0,
                                fibers: foodFibers = 0,
                                quantityDescription = "serving",
                                quantityNumber: catalogQuantityNumber = 1,
                                measurementsValue = 1,
                                unitOfMeasure: catalogUnitOfMeasure = "g",
                                _id: foodId,
                                type: foodType,
                                customQuantityNumber = 1,
                                customUnitOfMeasure = "g",
                              },
                              i
                            ) => {
                              return (
                                <div
                                  key={`${_id}-${foodId}-${i}`}
                                  className="detail-row"
                                >
                                  <span>{foodName}</span>
                                  <span>{brand}</span>
                                  <span>
                                    {customQuantityNumber}{" "}
                                    {parseUnitToShow(
                                      customQuantityNumber,
                                      foodType,
                                      customUnitOfMeasure,
                                      quantityDescription
                                    )}
                                    {formatCustomUnitMeasurementEquivalenceSuffix(
                                      customQuantityNumber,
                                      customUnitOfMeasure,
                                      {
                                        quantityNumber: catalogQuantityNumber,
                                        measurementsValue,
                                        unitOfMeasure: catalogUnitOfMeasure,
                                      }
                                    )}
                                  </span>
                                  <span>{foodProteins}</span>
                                  <span>{foodCarbs}</span>
                                  <span>{foodFats}</span>
                                  <span>{foodFibers}</span>
                                  <span>{foodCalories}</span>
                                  <span></span>
                                </div>
                              );
                            }
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </section>
  );
};

MasterMealsLibraryTable.propTypes = {
  data: PropTypes.array,
};

export default MasterMealsLibraryTable;
